import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "../../supabase";
import { shouldSkipSupabase } from "../../helpers/guestMode";
import { listFinanceActivity } from "../../helpers/financeActivity";
import { formatOpeningBalance } from "../../helpers/financeAccounts";
import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import { FinanceActivityDesktop } from "./FinanceDesktop";
import {
  FinanceBackButton,
  FINANCE_CARD_STYLE,
  FINANCE_HEADER_STYLE,
  FINANCE_PRIMARY_BTN_STYLE,
  FINANCE_SCREEN_STYLE,
  FINANCE_SECONDARY_BTN_STYLE,
  FINANCE_SUBTITLE_STYLE,
  FINANCE_TITLE_STYLE,
  FINANCE_TOOLBAR_STYLE,
} from "./financeUi";

const KIND_COLOR = {
  "Money In": "#4ee2a1",
  "Money Out": "#f87171",
  Transfer: "#c9a84c",
};

/**
 * Activity — owner-facing history of money movement.
 * Screen id remains finance_transactions for nav compatibility.
 */
export function FinanceTransactionsScreen({
  currentUser,
  onBack,
  onOpenMoneyIn,
  onOpenMoneyOut,
  onOpenTransfer,
}) {
  const isDesktop = useIsDesktopViewport();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (shouldSkipSupabase(currentUser)) {
      setRows([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const activity = await listFinanceActivity(supabase, { limit: 150 });
      setRows(activity);
      setError(null);
    } catch (err) {
      console.error("[Finance] activity load failed:", err);
      setError("Unable to load activity.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    void load();
  }, [load]);

  if (isDesktop) {
    return (
      <FinanceActivityDesktop
        rows={rows}
        loading={loading}
        error={error}
        onBack={onBack}
        onOpenMoneyIn={onOpenMoneyIn}
        onOpenMoneyOut={onOpenMoneyOut}
        onOpenTransfer={onOpenTransfer}
      />
    );
  }

  return (
    <div style={FINANCE_SCREEN_STYLE}>
      <FinanceBackButton onClick={onBack} />

      <div style={FINANCE_HEADER_STYLE}>
        <h1 style={FINANCE_TITLE_STYLE}>Activity</h1>
        <p style={FINANCE_SUBTITLE_STYLE}>
          History of money in, money out, and transfers.
        </p>
      </div>

      <div style={FINANCE_TOOLBAR_STYLE}>
        {typeof onOpenMoneyIn === "function" ? (
          <button
            type="button"
            style={FINANCE_PRIMARY_BTN_STYLE}
            onClick={onOpenMoneyIn}
          >
            Money In
          </button>
        ) : null}
        {typeof onOpenMoneyOut === "function" ? (
          <button
            type="button"
            style={FINANCE_SECONDARY_BTN_STYLE}
            onClick={onOpenMoneyOut}
          >
            Money Out
          </button>
        ) : null}
        {typeof onOpenTransfer === "function" ? (
          <button
            type="button"
            style={FINANCE_SECONDARY_BTN_STYLE}
            onClick={onOpenTransfer}
          >
            Transfer
          </button>
        ) : null}
      </div>

      {error ? (
        <p style={{ color: "#f87171", marginBottom: "12px" }}>{error}</p>
      ) : null}

      {loading ? (
        <p style={{ opacity: 0.7 }}>Loading…</p>
      ) : rows.length === 0 ? (
        <div style={FINANCE_CARD_STYLE}>
          <p style={{ margin: 0, opacity: 0.7 }}>
            No activity yet. Record money in, money out, or a transfer to get
            started.
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {rows.map((row) => {
            const accent = KIND_COLOR[row.kind] || "rgba(255,255,255,0.7)";
            const signed =
              row.kind === "Money Out"
                ? `−${formatOpeningBalance(row.amount, row.currency)}`
                : formatOpeningBalance(row.amount, row.currency);

            return (
              <div key={row.id} style={FINANCE_CARD_STYLE}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "12px",
                    alignItems: "flex-start",
                  }}
                >
                  <div>
                    <p
                      style={{
                        margin: "0 0 4px",
                        fontSize: "12px",
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: accent,
                      }}
                    >
                      {row.kind}
                    </p>
                    <p
                      style={{
                        margin: "0 0 4px",
                        fontSize: "15px",
                        fontWeight: 700,
                      }}
                    >
                      {row.description}
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "13px",
                        opacity: 0.65,
                      }}
                    >
                      {row.occurredOn} · {row.accountSummary}
                    </p>
                  </div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: 800,
                      color: accent,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {signed}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
